import '@fontsource/inter/400.css'
import '@fontsource/inter/500.css'
import '@fontsource/inter/600.css'
import '@fontsource/inter/700.css'
import '@fontsource/source-serif-4/400.css'
import '@fontsource/source-serif-4/500.css'
import '@fontsource/source-serif-4/600.css'
import '@fontsource/jetbrains-mono/400.css'
import '@fontsource/jetbrains-mono/500.css'
import '@fontsource/jetbrains-mono/600.css'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router-dom'
import { AppProviders } from './app/providers'
import { router } from './app/routes'
import './index.css'

/**
 * Cross-portal SSO handoff from eCareVoice (or any sibling app).
 *
 * When the AppSwitcher navigates here it appends:
 *   http://vitals.dev.ecarevoice.ai/#code=<signed_handoff_code>
 *
 * We POST the code to /api/auth/consume-handoff-code BEFORE React renders.
 * The backend verifies HMAC + 60s expiry and sets httpOnly session cookies,
 * so AuthProvider's GET /auth/me on first render already sees a valid session.
 *
 * On failure we swallow the error — the user will land on the login page.
 */
// Use the absolute API base so this works in production (S3/CloudFront frontend
// can't proxy /api/* — Jenkins sets VITE_API_BASE_URL to the full backend URL).
const _API_BASE = ((import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api').replace(/\/$/, '')

async function consumeHandoff(): Promise<void> {
  const hash = window.location.hash
  if (!hash.startsWith('#code=')) return
  try {
    // Exchange the signed handoff code for a Vitals session.
    // The backend verifies HMAC signature + 60s expiry before issuing cookies.
    const code = decodeURIComponent(hash.slice('#code='.length))
    await fetch(`${_API_BASE}/auth/consume-handoff-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ code }),
    })
  } catch {
    // Best-effort — failure just means user sees the login page.
  } finally {
    history.replaceState(null, '', window.location.pathname + window.location.search)
  }
}

// Await the handoff exchange before mounting React so the auth cookie is
// already present when AuthProvider calls GET /auth/me on first render.
void consumeHandoff().then(() => {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <AppProviders>
        <RouterProvider router={router} />
      </AppProviders>
    </StrictMode>,
  )
})
